import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wand2,
  Layers,
  Repeat,
  ShieldCheck,
  Gamepad2,
  Code2,
  ArrowRight,
  Check,
  Boxes,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Logo, LogoMark, LogoFull } from "@/components/Logo";
import HeroVideo from "@/components/HeroVideo";
import { abEvent } from "@/lib/ab";
import { useSeo } from "@/lib/seo";

const FEATURES = [
  { icon: Wand2, title: "Prompt to Music", desc: "Describe a scene, get a production-ready track in seconds.", span: "md:col-span-2" },
  { icon: Repeat, title: "Loopable", desc: "Seamless loops built for game engines." },
  { icon: Layers, title: "Stems", desc: "Isolated drums, bass & melody for mixing." },
  { icon: ShieldCheck, title: "Commercial License", desc: "Perpetual, monetization-ready rights with every track.", span: "md:col-span-2" },
];

const PRICING = [
  { name: "Free", price: "0", features: ["20 credits", "MP3 + WAV", "Basic license"] },
  { name: "Creator", price: "19", features: ["200 credits/mo", "Stems + loops", "Perpetual license"], hot: true },
  { name: "Pro", price: "49", features: ["1,000 credits/mo", "API access", "Analytics"] },
  { name: "API Business", price: "199", features: ["5,000 credits/mo", "Adaptive engine", "Unity & Unreal SDK"] },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => { abEvent("cta"); navigate(user ? "/dashboard" : "/login"); };
  useSeo({ path: "/" });

  return (
    <div className="min-h-screen bg-background grid-bg">
      {/* Nav */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo iconSize={26} textClassName="text-xl" />
            <span className="hidden sm:inline text-[10px] font-mono px-2 py-0.5 rounded bg-primary/15 text-primary ml-1">API</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#gaming" className="hover:text-foreground transition-colors">Adaptive</a>
            <Link to="/showcase" className="hover:text-foreground transition-colors" data-testid="nav-showcase">Showcase</Link>
            <Link to="/pricing" className="hover:text-foreground transition-colors" data-testid="nav-pricing">Pricing</Link>
          </nav>
          <div className="flex items-center gap-2">
            {!user && <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground px-3" data-testid="nav-login">Sign in</Link>}
            <Button onClick={go} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold glow-primary" data-testid="nav-cta">
              {user ? "Open Studio" : "Start free"}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1683064325134-3acfdef9c6d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-28 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> AI Soundtrack Engine for Games
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95]">
              Score every moment.<br />
              <span className="text-primary">Generated in real time.</span>
            </h1>
            <p className="text-lg text-muted-foreground mt-6 max-w-xl">
              MusicFlow StudioAi turns a prompt into loopable soundtracks, stems and adaptive game music — with commercial licensing and an SDK for Unity, Unreal & Godot.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={go} size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold glow-primary" data-testid="hero-cta">
                Generate your first track <ArrowRight size={18} className="ml-1.5" />
              </Button>
              <Button onClick={() => { abEvent("cta"); navigate(user ? "/dashboard/gaming" : "/login"); }} size="lg" variant="outline" className="border-border bg-card/50" data-testid="hero-gaming-cta">
                <Gamepad2 size={18} className="mr-1.5" /> See adaptive demo
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }} className="hidden lg:flex justify-center">
            <HeroVideo fallback={<LogoFull width={420} className="drop-shadow-[0_0_40px_rgba(124,58,237,0.35)]" />} />
          </motion.div>
        </div>
        <div className="relative lg:hidden max-w-7xl mx-auto px-6 -mt-16 pb-16">
          <HeroVideo compact />
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="font-display text-4xl font-extrabold mb-3">Beyond a music generator</h2>
        <p className="text-muted-foreground mb-10 max-w-xl">An audio infrastructure built for developers, studios and creators.</p>
        <div className="grid md:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className={`rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition-colors ${f.span || ""}`}>
              <f.icon className="text-primary mb-4" size={26} />
              <h3 className="font-display text-xl font-bold mb-1">{f.title}</h3>
              <p className="text-muted-foreground text-sm">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gaming */}
      <section id="gaming" className="relative overflow-hidden border-y border-border">
        <img src="https://images.unsplash.com/photo-1612287230202-1ff1d85d1bdf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1.5 rounded-full border border-secondary/30 bg-secondary/10 text-secondary mb-6">
              <Gamepad2 size={13} /> The strategic feature
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold leading-tight">Music that reacts to your game.</h2>
            <p className="text-muted-foreground mt-4 max-w-lg">
              Feed MusicFlow StudioAi your game state — exploration, tension, combat, boss — and the score adapts intensity, tempo and instrumentation in real time.
            </p>
            <div className="font-mono text-sm bg-card border border-border rounded-xl p-5 mt-8 text-primary leading-relaxed">
              <span className="text-muted-foreground">// live control</span><br />
              music.setIntensity(0.8);<br />
              music.setMood("dark");<br />
              music.transition("combat");
            </div>
            <div className="flex gap-3 mt-6">
              <Badge2 icon={Boxes}>Unity SDK</Badge2>
              <Badge2 icon={Boxes}>Unreal SDK</Badge2>
              <Badge2 icon={Code2}>REST API</Badge2>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {["Exploration 20%", "Tension 50%", "Combat 80%", "Boss 100%"].map((s, i) => (
              <div key={s} className="rounded-2xl border border-border bg-card p-6" style={{ borderColor: i === 3 ? "hsl(var(--secondary))" : undefined }}>
                <div className="font-mono text-xs text-muted-foreground mb-2">STATE</div>
                <div className="font-display text-lg font-bold">{s.split(" ")[0]}</div>
                <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: s.split(" ")[1], background: i >= 2 ? "hsl(var(--secondary))" : "hsl(var(--primary))" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="font-display text-4xl font-extrabold mb-3">Simple, credit-based pricing</h2>
        <p className="text-muted-foreground mb-10">Start free. Scale from creator to enterprise.</p>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
          {PRICING.map((p) => (
            <div key={p.name} className={`relative rounded-2xl border p-6 flex flex-col ${p.hot ? "border-primary bg-primary/[0.04] glow-primary" : "border-border bg-card"}`}>
              {p.hot && <div className="absolute -top-2.5 left-6 text-[10px] font-mono px-2 py-0.5 rounded bg-primary text-primary-foreground">POPULAR</div>}
              <h3 className="font-display text-xl font-bold">{p.name}</h3>
              <div className="mt-2 mb-5"><span className="font-display text-4xl font-extrabold">€{p.price}</span>{p.price !== "0" && <span className="text-muted-foreground text-sm">/mo</span>}</div>
              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((f) => <li key={f} className="flex gap-2 text-sm text-muted-foreground"><Check size={15} className="text-primary shrink-0 mt-0.5" /> {f}</li>)}
              </ul>
              <Button onClick={go} className={p.hot ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-muted hover:bg-muted/70"} data-testid={`landing-plan-${p.name.toLowerCase().replace(" ", "-")}`}>Get started</Button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-28">
        <div className="rounded-3xl border border-border bg-card p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-50" />
          <div className="relative">
            <LogoFull width={260} className="mx-auto mb-6" />
            <h2 className="font-display text-4xl font-extrabold">Build your game's sound.</h2>
            <p className="text-muted-foreground mt-3 mb-8 max-w-md mx-auto">Get 20 free credits. No card required.</p>
            <Button onClick={go} size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold glow-primary" data-testid="footer-cta">
              Start generating <ArrowRight size={18} className="ml-1.5" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><LogoMark size={18} /> MusicFlow StudioAi · Insert.si.on</div>
          <div className="flex items-center gap-5 text-xs"><Link to="/pricing" className="hover:text-foreground" data-testid="footer-pricing">Pricing</Link><Link to="/showcase" className="hover:text-foreground" data-testid="footer-showcase">Showcase</Link><Link to="/terms" className="hover:text-foreground" data-testid="footer-terms">Terms</Link><Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy</Link><span className="font-mono">© 2026</span></div>
        </div>
      </footer>
    </div>
  );
}

const Badge2 = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border border-border bg-card text-muted-foreground">
    <Icon size={13} /> {children}
  </span>
);
